import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Phone, Building2, Briefcase, Globe, MapPin,
  Lock, Eye, EyeOff, Save, CheckCircle2, Zap, Shield, Star, Award } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const INDUSTRIES = ['Technology','Finance','Healthcare','Real Estate','Retail','Manufacturing','Education','Marketing','Legal','Construction','Hospitality','E-commerce','SaaS','Consulting','Other'];

const PLAN_INFO = {
  free:       { label:'Free',       color:'var(--text3)',  bg:'var(--bg3)',        credits:50,    badge:'badge-gray'   },
  individual: { label:'Individual', color:'var(--blue)',   bg:'var(--blue-bg)',    credits:500,   badge:'badge-blue'   },
  pro:        { label:'Pro',        color:'var(--accent)', bg:'var(--accent-bg)',  credits:1000,  badge:'badge-accent' },
  pro_plus:   { label:'Pro Plus',   color:'var(--purple)', bg:'var(--purple-bg)',  credits:3000,  badge:'badge-purple' },
  enterprise: { label:'Enterprise', color:'var(--gold)',   bg:'var(--gold-bg)',    credits:99999, badge:'badge-gold'   },
};

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: user?.first_name||'', lastName:  user?.last_name||'',
    company:   user?.company||'',    jobTitle:  user?.job_title||'',
    industry:  user?.industry||'',   phone:     user?.phone||'',
    city:      user?.city||'',       country:   user?.country||'India',
    website:   user?.website||'',
  });
  const [pwForm, setPwForm]   = useState({ currentPassword:'', newPassword:'', confirmPassword:'' });
  const [showPw, setShowPw]   = useState({ current:false, new:false });
  const [saving, setSaving]   = useState(false);
  const [savingPw,setSavingPw]= useState(false);
  const [activeTab, setTab]   = useState('profile');

  const plan     = PLAN_INFO[user?.plan||'free'] || PLAN_INFO.free;
  const credits  = user?.search_credits ?? 0;
  const maxCred  = plan.credits;
  const creditPct= Math.min(100, (credits/maxCred)*100);
  const initials = `${user?.first_name?.[0]||''}${user?.last_name?.[0]||''}`.toUpperCase();

  const saveProfile = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const r = await api.put('/auth/profile', { firstName:form.firstName, lastName:form.lastName, company:form.company, jobTitle:form.jobTitle, industry:form.industry, phone:form.phone, city:form.city, country:form.country, website:form.website });
      updateUser(r.data.user);
      toast.success('Profile updated!');
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  const changePassword = async e => {
    e.preventDefault();
    if (pwForm.newPassword!==pwForm.confirmPassword) { toast.error("Passwords don't match"); return; }
    if (pwForm.newPassword.length<8) { toast.error('Password must be at least 8 characters'); return; }
    setSavingPw(true);
    try {
      await api.put('/auth/change-password', { currentPassword:pwForm.currentPassword, newPassword:pwForm.newPassword });
      toast.success('Password changed!');
      setPwForm({ currentPassword:'', newPassword:'', confirmPassword:'' });
    } catch(err) { toast.error(err.response?.data?.message||'Failed'); }
    finally { setSavingPw(false); }
  };

  const F = ({ k, label, type='text', placeholder, icon, form:f='profile' }) => {
    const val    = f==='profile' ? form[k] : pwForm[k];
    const setter = f==='profile' ? (v=>setForm(x=>({...x,[k]:v}))) : (v=>setPwForm(x=>({...x,[k]:v})));
    const isPw   = k.toLowerCase().includes('password');
    const pwKey  = k==='currentPassword'?'current':'new';
    const shown  = showPw[pwKey];
    return (
      <div className="form-group">
        <label className="form-label">{label}</label>
        <div className="input-group">
          {icon&&React.cloneElement(icon,{size:14,className:'input-icon'})}
          <input className="input" style={icon?{paddingLeft:38}:{}} type={isPw?(shown?'text':'password'):type} placeholder={placeholder} value={val} onChange={e=>setter(e.target.value)}/>
          {isPw&&<button type="button" className="input-icon-right" onClick={()=>setShowPw(x=>({...x,[pwKey]:!x[pwKey]}))}>{shown?<EyeOff size={14}/>:<Eye size={14}/>}</button>}
        </div>
      </div>
    );
  };

  const TABS = [
    { id:'profile',  label:'Profile Info' },
    { id:'security', label:'Security' },
    { id:'plan',     label:'Plan & Credits' },
  ];

  return (
    <div style={{ maxWidth:900, margin:'0 auto', padding:'20px' }}>
      <style>{`
        @media(max-width:700px){ .profile-layout{grid-template-columns:1fr!important;} .profile-sidebar{display:none!important;} }
      `}</style>

      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, marginBottom:4 }}>My Profile</h1>
        <p style={{ color:'var(--text3)', fontSize:14 }}>Manage your account details and security settings</p>
      </div>

      <div className="profile-layout" style={{ display:'grid', gridTemplateColumns:'220px 1fr', gap:20, alignItems:'start' }}>

        {/* Sidebar */}
        <div className="profile-sidebar" style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {/* Avatar card */}
          <div className="card" style={{ textAlign:'center', padding:'24px 20px' }}>
            <div style={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,var(--accent),var(--accent2))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, fontWeight:800, color:'#fff', margin:'0 auto 14px', boxShadow:'0 4px 20px rgba(225,29,72,.3)' }}>
              {initials}
            </div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, marginBottom:3 }}>{user?.first_name} {user?.last_name}</div>
            <div style={{ fontSize:12.5, color:'var(--text3)', marginBottom:10, wordBreak:'break-all' }}>{user?.email}</div>
            <span className={`badge ${plan.badge}`} style={{ textTransform:'uppercase', letterSpacing:.5 }}>{plan.label}</span>
          </div>

          {/* Stats */}
          <div className="card" style={{ padding:'16px' }}>
            {[
              ['Searches',   (user?.total_searches||0).toLocaleString()],
              ['Credits',    credits.toLocaleString()],
              ['Leads Found',(user?.total_leads_found||0).toLocaleString()],
              ['Role',       user?.role||'user'],
            ].map(([k,v])=>(
              <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                <span style={{ color:'var(--text3)' }}>{k}</span>
                <span style={{ fontWeight:600, textTransform:'capitalize' }}>{v}</span>
              </div>
            ))}
            <div style={{ paddingTop:10, fontSize:12, color:'var(--text3)', textAlign:'center' }}>
              Member since {user?.created_at?new Date(user.created_at).toLocaleDateString('en-IN',{month:'short',year:'numeric'}):'—'}
            </div>
          </div>

          {/* Nav */}
          <div className="card" style={{ padding:8 }}>
            {TABS.map(t=>(
              <button key={t.id} onClick={()=>setTab(t.id)}
                style={{ width:'100%', padding:'9px 12px', borderRadius:7, border:'none', background:activeTab===t.id?'var(--accent-bg)':'transparent', color:activeTab===t.id?'var(--accent)':'var(--text2)', fontWeight:activeTab===t.id?700:500, fontSize:13.5, cursor:'pointer', textAlign:'left', marginBottom:2 }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div>
          {/* Mobile tabs */}
          <div className="tabs" style={{ marginBottom:16, display:'none' }} id="profile-tabs">
            {TABS.map(t=><button key={t.id} className={`tab${activeTab===t.id?' active':''}`} onClick={()=>setTab(t.id)}>{t.label}</button>)}
          </div>

          {/* Profile tab */}
          {activeTab==='profile'&&(
            <div className="card">
              <div className="card-header"><div className="card-title"><User size={16} style={{ color:'var(--accent)',verticalAlign:'middle',marginRight:8 }}/>Personal Information</div></div>
              <form onSubmit={saveProfile} style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <div className="form-row">
                  <F k="firstName" label="First Name *" placeholder="John" icon={<User/>}/>
                  <F k="lastName"  label="Last Name *"  placeholder="Doe"  icon={<User/>}/>
                </div>
                <div className="form-row">
                  <F k="company"  label="Company"    placeholder="Acme Corp"     icon={<Building2/>}/>
                  <F k="jobTitle" label="Job Title"  placeholder="Sales Manager" icon={<Briefcase/>}/>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Industry</label>
                    <select className="select" value={form.industry} onChange={e=>setForm(f=>({...f,industry:e.target.value}))}>
                      <option value="">Select…</option>
                      {INDUSTRIES.map(i=><option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <F k="phone" label="Phone" placeholder="+91 98765 43210" icon={<Phone/>}/>
                </div>
                <div className="form-row">
                  <F k="city"    label="City"    placeholder="Mumbai" icon={<MapPin/>}/>
                  <F k="country" label="Country" placeholder="India"  icon={<Globe/>}/>
                </div>
                <F k="website" label="Company Website" type="url" placeholder="https://yourcompany.com" icon={<Globe/>}/>
                <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:4 }}>
                  <button className="btn btn-primary" type="submit" disabled={saving}>
                    {saving?<><div className="spinner spinner-sm"/>Saving…</>:<><Save size={14}/>Save Profile</>}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Security tab */}
          {activeTab==='security'&&(
            <div className="card">
              <div className="card-header"><div className="card-title"><Lock size={16} style={{ color:'var(--accent)',verticalAlign:'middle',marginRight:8 }}/>Change Password</div></div>
              <form onSubmit={changePassword} style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <F k="currentPassword" label="Current Password" placeholder="Enter current password" icon={<Lock/>} form="pw"/>
                <div className="form-row">
                  <F k="newPassword"     label="New Password"     placeholder="Min 8 characters" icon={<Lock/>} form="pw"/>
                  <F k="confirmPassword" label="Confirm Password" placeholder="Repeat password"  icon={<Lock/>} form="pw"/>
                </div>
                <div style={{ background:'var(--bg3)', borderRadius:8, padding:'12px 14px', display:'flex', flexDirection:'column', gap:6 }}>
                  {['At least 8 characters','One uppercase letter','One number','Never share your password'].map((t,i)=>(
                    <div key={i} style={{ display:'flex', gap:8, fontSize:12.5, color:'var(--text3)', alignItems:'center' }}>
                      <CheckCircle2 size={12} style={{ color:'var(--green)', flexShrink:0 }}/>{t}
                    </div>
                  ))}
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  <button className="btn btn-primary" type="submit" disabled={savingPw}>
                    {savingPw?<><div className="spinner spinner-sm"/>Updating…</>:<><Lock size={14}/>Update Password</>}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Plan tab */}
          {activeTab==='plan'&&(
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {/* Current plan */}
              <div className="card">
                <div className="card-header"><div className="card-title"><Award size={16} style={{ color:'var(--accent)',verticalAlign:'middle',marginRight:8 }}/>Current Plan</div></div>
                <div style={{ display:'flex', alignItems:'center', gap:16, padding:'8px 0', flexWrap:'wrap' }}>
                  <div style={{ width:56, height:56, borderRadius:14, background:plan.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:26 }}>
                    {user?.plan==='enterprise'?'🏢':user?.plan==='pro_plus'?'⚡':user?.plan==='pro'?'🚀':user?.plan==='individual'?'👤':'🆓'}
                  </div>
                  <div>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, marginBottom:4 }}>{plan.label} Plan</div>
                    <span className={`badge ${plan.badge}`} style={{ fontSize:12 }}>{user?.plan||'free'}</span>
                  </div>
                </div>
              </div>

              {/* Credits */}
              <div className="card">
                <div className="card-header"><div className="card-title"><Zap size={16} style={{ color:'var(--accent)',verticalAlign:'middle',marginRight:8 }}/>Search Credits</div></div>
                <div style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, fontWeight:600, marginBottom:8 }}>
                    <span>{credits.toLocaleString()} remaining</span>
                    <span style={{ color:'var(--text3)' }}>{maxCred===99999?'Unlimited':maxCred.toLocaleString()} total</span>
                  </div>
                  <div style={{ height:10, background:'var(--border)', borderRadius:5, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${creditPct}%`, background:creditPct>50?'var(--green)':creditPct>20?'var(--gold)':'var(--red)', borderRadius:5, transition:'width .5s' }}/>
                  </div>
                  {credits<=10&&<div style={{ fontSize:12.5, color:'var(--red)', marginTop:6 }}>⚠️ Low credits! Upgrade your plan to get more.</div>}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10 }}>
                  {[
                    { label:'Total Searches',   val:(user?.total_searches||0).toLocaleString()   },
                    { label:'Total Leads Found', val:(user?.total_leads_found||0).toLocaleString()},
                  ].map(s=>(
                    <div key={s.label} style={{ background:'var(--bg3)', borderRadius:8, padding:'12px', textAlign:'center' }}>
                      <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:800, color:'var(--accent)', marginBottom:3 }}>{s.val}</div>
                      <div style={{ fontSize:12, color:'var(--text3)' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upgrade CTA */}
              {user?.plan!=='enterprise'&&(
                <div style={{ background:'linear-gradient(135deg,var(--accent),var(--accent2))', borderRadius:14, padding:'24px', color:'#fff', textAlign:'center' }}>
                  <div style={{ fontSize:24, marginBottom:10 }}>🚀</div>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:800, marginBottom:8 }}>Upgrade Your Plan</div>
                  <div style={{ fontSize:14, opacity:.85, marginBottom:20, lineHeight:1.6 }}>
                    Get more credits, team features, and unlock the full power of LeadRadar.
                  </div>
                  <button className="btn btn-lg" style={{ background:'#fff', color:'var(--accent)', border:'none', fontWeight:700 }}
                    onClick={()=>navigate('/team')}>
                    View Plans →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media(max-width:700px){
          #profile-tabs { display:flex!important; }
          .profile-sidebar { display:none!important; }
          .profile-layout { grid-template-columns:1fr!important; }
        }
      `}</style>
    </div>
  );
}